import { ref } from 'vue'

type NotificationType = 'success' | 'error' | 'warning' | 'info'

interface Notification {
  id: number
  type: NotificationType
  title: string
  message: string
  duration: number
  visible: boolean
}

interface ShowOptions {
  type?: NotificationType
  title?: string
  message: string
  duration?: number
}

const notifications = ref<Notification[]>([])

export const useNotification = () => {
  const show = ({ type = 'info', title = '', message, duration = 5000 }: ShowOptions): number => {
    const id = Date.now()
    
    const defaultTitles: Record<NotificationType, string> = {
      success: 'Succès',
      error: 'Erreur',
      warning: 'Attention',
      info: 'Information'
    }
    
    const notification: Notification = {
      id,
      type,
      title: title || defaultTitles[type],
      message,
      duration,
      visible: true
    }
    
    notifications.value.push(notification)
    
    if (duration > 0) {
      setTimeout(() => {
        close(id)
      }, duration)
    }
    
    return id
  }
  
  const close = (id: number): void => {
    const index = notifications.value.findIndex(n => n.id === id)
    if (index !== -1) {
      notifications.value[index].visible = false
      setTimeout(() => {
        notifications.value.splice(index, 1)
      }, 300)
    }
  }
  
  const success = (message: string, title = '', duration = 5000): number => {
    return show({ type: 'success', title, message, duration })
  }
  
  const error = (message: string, title = '', duration = 6000): number => {
    return show({ type: 'error', title, message, duration })
  }
  
  const warning = (message: string, title = '', duration = 5000): number => {
    return show({ type: 'warning', title, message, duration })
  }
  
  const info = (message: string, title = '', duration = 5000): number => {
    return show({ type: 'info', title, message, duration })
  }
  
  return {
    notifications,
    show,
    close,
    success,
    error,
    warning,
    info
  }
}
